import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, ElementRef, OnDestroy } from '@angular/core';
import {
    MatTreeFlatDataSource,
    MatTreeFlattener,
} from '@angular/material/tree';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import module from 'app/modules/annulus-pressure-record/annulus-pressure-record.module';
import { AnnulusPressureResponse, FieldData } from '../../model/apr-dashboard';
import { AprService } from '../../services/annulus-pressure-record.service';

am4core.useTheme(am4themes_animated);

interface TreeNode {
    name: string;
    longName: string;
    id: string;
    level: 'Field' | 'Platform' | 'WellString';
    children?: TreeNode[];
}

// Tree data will be loaded from API

/** Flat node with expandable and level information */
interface FlatTreeNode {
    expandable: boolean;
    name: string;
    longName: string;
    id: string;
    nodeLevel: 'Field' | 'Platform' | 'WellString';
    level: number;
}

@Component({
    selector: 'app-apr-dashboard',
    standalone: true,
    imports: module,
    templateUrl: './apr-dashboard.component.html',
    styleUrl: './apr-dashboard.component.scss',
})
export class AprDashboardPage implements OnDestroy {
    private readonly overallAnnulusColorMap: Record<string, am4core.Color> = {
        A: am4core.color('#3B82F6'), // Blue
        B: am4core.color('#10B981'), // Emerald
        C: am4core.color('#F59E0B'), // Amber
        D: am4core.color('#F43F5E'), // Rose
    };

    private transformer = (node: TreeNode, level: number) => {
        return {
            expandable: !!node.children && node.children.length > 0,
            name: node.name,
            longName: node.longName,
            id: node.id,
            nodeLevel: node.level,
            level: level,
        };
    };

    treeControl = new FlatTreeControl<FlatTreeNode>(
        (node) => node.level,
        (node) => node.expandable,
    );

    treeFlattener = new MatTreeFlattener(
        this.transformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children,
    );

    dataSource = new MatTreeFlatDataSource(
        this.treeControl,
        this.treeFlattener,
    );

    public selectedNode: any = '';
    public totalWells: number = 0;
    public totalStrings: number = 0;
    public annulusPressureCharts: any[] = [];
    public selectedTabIndex: number = 0;
    public aprExceedMawopData: any = null;
    public annulusExceedMawopData: any = null;
    public hasOverallData: boolean = false;
    private currentChartResponse: AnnulusPressureResponse | null = null;
    private currentWellStringId: string | null = null;
    private overallChartInstance: any = null;
    private overallChartInitialized: boolean = false;
    private readonly individualChartInstances: Map<number, any> = new Map();
    public selectedYear: number | null = null;
    public yearInputValue: string = '';

    constructor(
        private elementRef: ElementRef,
        private aprService: AprService,
    ) {
        this.loadPlatformsData();
    }

    ngOnInit(): void {
        // Load MAWOP exceed data first; charts will be created once data arrives
        this.loadMawopExceedData();
    }

    private createAprManagementBarChart() {
        // Annulus MAWOP distribution bar chart (vertical)
        if (!this.annulusExceedMawopData) return;

        const chart = am4core.create(
            'aprManagementBarChart',
            am4charts.XYChart,
        );
        chart.paddingBottom = 0;

        chart.data = [
            {
                activity: 'Annulus A',
                completed:
                    this.annulusExceedMawopData.totalAnnulusAExceeding || 0,
                color: am4core.color('#3B82F6'),
            },
            {
                activity: 'Annulus B',
                completed:
                    this.annulusExceedMawopData.totalAnnulusBExceeding || 0,
                color: am4core.color('#10B981'),
            },
            {
                activity: 'Annulus C',
                completed:
                    this.annulusExceedMawopData.totalAnnulusCExceeding || 0,
                color: am4core.color('#F59E0B'),
            },
            {
                activity: 'Annulus D',
                completed:
                    this.annulusExceedMawopData.totalAnnulusDExceeding || 0,
                color: am4core.color('#F43F5E'),
            },
        ];

        // Vertical: category on X-axis, values on Y-axis
        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'activity';
        categoryAxis.renderer.labels.template.fontSize = 12;
        categoryAxis.renderer.labels.template.fill = am4core.color('#000');
        categoryAxis.renderer.grid.template.strokeOpacity = 0;

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.renderer.grid.template.strokeOpacity = 1;
        valueAxis.renderer.grid.template.stroke = am4core.color('#e5e7eb');
        valueAxis.renderer.ticks.template.strokeOpacity = 0;
        valueAxis.renderer.line.strokeOpacity = 0;
        valueAxis.renderer.labels.template.fontSize = 11;
        valueAxis.renderer.labels.template.fill = am4core.color('#000');
        valueAxis.renderer.disabled = true; // Hide Y-axis

        const series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = 'completed';
        series.dataFields.categoryX = 'activity';
        series.name = 'Annulus Exceed MAWOP';
        series.columns.template.width = am4core.percent(50);
        series.columns.template.propertyFields.fill = 'color';
        series.strokeWidth = 0;
        series.columns.template.tooltipText = '{activity}: [bold]{valueY}[/]';
        series.tooltip.fontSize = 12;
        series.tooltip.background.cornerRadius = 4;
        series.tooltip.background.strokeOpacity = 0;
        series.tooltip.pointerOrientation = 'vertical';
        series.columns.template.cursorOverStyle =
            am4core.MouseCursorStyle.pointer;
        const hoverState = series.columns.template.states.create('hover');
        hoverState.properties.fillOpacity = 0.8;

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    private loadMawopExceedData(): void {
        // Load APR exceed MAWOP count first
        this.aprService.getAprExceedMawopCount().subscribe({
            next: (response) => {
                if (response.isSuccess) {
                    this.aprExceedMawopData = response.data;
                }
            },
            error: (error) => {
                console.error('Failed to load APR exceed MAWOP count:', error);
            },
        });

        // Load Annulus exceed MAWOP count
        this.aprService.getAprAnnulusExceedMawopCount().subscribe({
            next: (response) => {
                if (response.isSuccess) {
                    this.annulusExceedMawopData = response.data;
                    // Create Annulus bar chart once data ready
                    this.createAprManagementBarChart();
                }
            },
            error: (error) => {
                console.error(
                    'Failed to load Annulus exceed MAWOP count:',
                    error,
                );
            },
        });
    }

    openGraph(
        wellStringId: string,
        nodeLevel?: 'Field' | 'Platform' | 'WellString',
    ) {
        // Find the selected node by wellStringId to get the name for display
        const selectedNode = this.treeControl.dataNodes.find(
            (node) => node.id === wellStringId,
        );
        this.selectedNode = selectedNode ? selectedNode.name : wellStringId;

        // Only fetch annulus pressure data for well string nodes (last child only)
        if (nodeLevel === 'WellString' && wellStringId) {
            this.currentWellStringId = wellStringId;
            this.loadAnnulusPressureData(wellStringId);
        } else {
            this.currentWellStringId = null;
            // For field or platform nodes, show no data message
            this.createDefaultChart();
        }
    }

    private loadAnnulusPressureData(wellStringId: string): void {
        this.aprService
            .getAprWellStringAnnulusPressure(wellStringId, this.selectedYear)
            .subscribe({
                next: (response: AnnulusPressureResponse) => {
                    // Check if there are plottable points in any returned series.
                    if (this.hasRenderableSeriesData(response)) {
                        this.createAnnulusPressureChart(response);
                        return;
                    }

                    this.annulusPressureCharts = [];
                    this.currentChartResponse = null;
                    this.createDefaultChart();
                },
                error: (error) => {
                    console.error(
                        'Failed to load annulus pressure data:',
                        error,
                    );
                    // Show no data message on error
                    this.createDefaultChart();
                },
            });
    }

    private hasRenderableSeriesData(
        response: AnnulusPressureResponse,
    ): boolean {
        if (!response?.series?.length) {
            return false;
        }

        return response.series.some((series: any) =>
            (series?.data || []).some(
                (point: any) =>
                    point?.pressure !== null && point?.pressure !== undefined,
            ),
        );
    }

    onYearFilterChange(year: number | null): void {
        this.selectedYear = year;
        this.yearInputValue = year === null ? '' : String(year);

        if (this.currentWellStringId) {
            this.loadAnnulusPressureData(this.currentWellStringId);
        }
    }

    applyYearInput(rawYear: string): void {
        const normalizedYear = (rawYear || '').trim();

        if (normalizedYear === '') {
            this.onYearFilterChange(null);
            return;
        }

        if (!/^\d{4}$/.test(normalizedYear)) {
            this.yearInputValue =
                this.selectedYear === null ? '' : String(this.selectedYear);
            return;
        }

        this.onYearFilterChange(Number(normalizedYear));
    }

    private createAnnulusPressureChart(
        response: AnnulusPressureResponse,
    ): void {
        // Store response for later use
        this.currentChartResponse = response;
        this.hasOverallData = true;

        // Ensure structural *ngIf directives in tab 0 know data exists
        this.annulusPressureCharts = response.series.map((seriesItem) => ({
            name: seriesItem.name,
            data: seriesItem.data,
        }));

        if (this.selectedTabIndex === 0) {
            this.createIndividualSeriesCharts(response.series);
        } else {
            setTimeout(() => {
                this.createOverallChart(response);
            }, 50);
        }
    }

    private createOverallChart(response: AnnulusPressureResponse): void {
        // Check if chartdiv container exists
        const chartContainer = document.getElementById('chartdiv');
        if (!chartContainer) {
            return; // Exit safely, the tab change event will handle recreation if opened
        }

        // Dispose of previous chart if it exists
        if (this.overallChartInstance) {
            this.overallChartInstance.dispose();
            this.overallChartInstance = null;
        }

        // Create chart instance
        const chart = am4core.create('chartdiv', am4charts.XYChart);
        this.overallChartInstance = chart;
        this.overallChartInitialized = true;
        chart.width = am4core.percent(100);
        chart.height = am4core.percent(100);
        chart.logo.disabled = true;

        // Transform API data to chart format
        // Create a map of all dates to ensure consistent data points
        const dateMap = new Map<string, any>();

        // Process each series
        for (const series of response.series) {
            for (const dataPoint of series.data) {
                const dateKey = dataPoint.date;
                if (!dateMap.has(dateKey)) {
                    dateMap.set(dateKey, { date: new Date(dataPoint.date) });
                }
                // Add pressure data to the date entry
                dateMap.get(dateKey)[series.name] = dataPoint.pressure;
            }
        }

        // Convert map to array
        chart.data = Array.from(dateMap.values());

        // Guard against empty chart render even when series names exist.
        if (!chart.data.length) {
            this.hasOverallData = false;
            this.createDefaultChart();
            return;
        }

        // Create category axis
        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.dateFormats.setKey('day', 'dd MMM yyyy');
        dateAxis.periodChangeDateFormats.setKey('day', 'dd MMM yyyy');
        dateAxis.renderer.labels.template.rotation = -45;
        dateAxis.renderer.labels.template.horizontalCenter = 'right';
        dateAxis.renderer.labels.template.verticalCenter = 'middle';
        dateAxis.renderer.labels.template.fill = am4core.color('#000');
        dateAxis.renderer.minGridDistance = 20;

        // Create value axis
        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.renderer.inversed = false;
        valueAxis.title.text = 'Pressure (PSI)';
        valueAxis.renderer.minLabelPosition = 0.01;
        valueAxis.renderer.labels.template.fill = am4core.color('#000');

        // Calculate max value to accommodate MAWOP
        let maxAxisValue = 0;
        for (const series of response.series) {
            const maxPressure = Math.max(
                ...series.data.map((d: any) => d.pressure || 0),
            );
            const maxMawop = Math.max(
                ...series.data.map((d: any) => d.mawopPressure || 0),
            );
            maxAxisValue = Math.max(maxAxisValue, maxPressure, maxMawop);
        }
        valueAxis.max = maxAxisValue * 1.1; // Add 10% padding

        // Fallback palette (non-red spectrum)
        const fallbackColors = [
            am4core.color('#3B82F6'), // Blue
            am4core.color('#10B981'), // Emerald
            am4core.color('#F59E0B'), // Amber
            am4core.color('#F43F5E'), // Rose
            am4core.color('#0EA5E9'), // Sky
        ];

        // Add horizontal reference lines for mawopPressure
        for (let i = 0; i < response.series.length; i++) {
            const seriesData = response.series[i];
            // Get the first mawopPressure value (assuming it's consistent for the series)
            const mawopPressure = seriesData.data.find(
                (d: any) =>
                    d.mawopPressure !== null && d.mawopPressure !== undefined,
            )?.mawopPressure;

            if (mawopPressure) {
                const range = valueAxis.axisRanges.create();
                range.value = mawopPressure;

                // Horizontal target line
                range.grid.visible = true;
                range.grid.strokeOpacity = 1;
                range.grid.strokeWidth = 2;
                range.grid.stroke = am4core.color('#FF0000');
                range.grid.strokeDasharray = '5,5';

                // Label positioned inside plot area, above the line
                range.label.text = `MAWOP ${
                    seriesData.name.split('-')[0]
                }: ${mawopPressure} PSI`;
                range.label.fontSize = 11;
                range.label.fontWeight = 'bold';
                range.label.fill = am4core.color('#FF0000');

                range.label.inside = true; // render inside plot, not on axis
                range.label.location = 1; // anchor at top edge of the range (above the line)
                range.label.horizontalCenter = 'middle';
                range.label.textAlign = 'middle';
                range.label.dy = -10; // lift it above the line; tweak to your spacing
                range.label.padding(0, 6, 0, 6); // small horizontal padding
                // Optional: ensure vertical alignment is consistent
                // (Some themes respect valign; if not, dy is enough)
                // @ts-ignore
                range.label.valign = 'top';
            }
        }

        // Create series dynamically based on API response
        for (let i = 0; i < response.series.length; i++) {
            const seriesData = response.series[i];
            const seriesColor =
                this.getOverallSeriesColor(seriesData.name) ||
                fallbackColors[i % fallbackColors.length];
            const series = chart.series.push(new am4charts.LineSeries());
            series.dataFields.valueY = seriesData.name;
            series.dataFields.dateX = 'date';
            series.name = seriesData.name;
            series.strokeWidth = 2;
            series.stroke = seriesColor;

            // Create and configure bullets (dots)
            const bullet = series.bullets.push(new am4charts.CircleBullet());
            bullet.circle.radius = 4;
            bullet.circle.strokeWidth = 2;
            bullet.circle.fill = seriesColor;
            bullet.circle.stroke = am4core.color('#fff');

            series.tooltipText = '{name}: {valueY} PSI';
            series.legendSettings.valueText = '{valueY}';
            series.visible = true;
        }

        // Add chart cursor
        chart.cursor = new am4charts.XYCursor();
        chart.cursor.behavior = 'zoomY';

        // Add legend
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.contentAlign = 'center';
        chart.legend.align = 'center';
        chart.legend.width = am4core.percent(100);
        chart.legend.marginTop = 20;
        chart.legend.marginLeft = 92;
        chart.legend.labels.template.fill = am4core.color('#000');

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    private getOverallSeriesColor(seriesName: string): am4core.Color | null {
        const normalizedName = (seriesName || '').toUpperCase();

        if (normalizedName.includes('ANNULUS A')) {
            return this.overallAnnulusColorMap.A;
        }

        if (normalizedName.includes('ANNULUS B')) {
            return this.overallAnnulusColorMap.B;
        }

        if (normalizedName.includes('ANNULUS C')) {
            return this.overallAnnulusColorMap.C;
        }

        if (normalizedName.includes('ANNULUS D')) {
            return this.overallAnnulusColorMap.D;
        }

        if (normalizedName.startsWith('A') || normalizedName.includes(' A-')) {
            return this.overallAnnulusColorMap.A;
        }

        if (normalizedName.startsWith('B') || normalizedName.includes(' B-')) {
            return this.overallAnnulusColorMap.B;
        }

        if (normalizedName.startsWith('C') || normalizedName.includes(' C-')) {
            return this.overallAnnulusColorMap.C;
        }

        if (normalizedName.startsWith('D') || normalizedName.includes(' D-')) {
            return this.overallAnnulusColorMap.D;
        }

        return null;
    }

    private createIndividualSeriesCharts(series: any[]): void {
        // Initialize the charts array
        this.annulusPressureCharts = series.map((seriesItem) => ({
            name: seriesItem.name,
            data: seriesItem.data,
        }));

        // Create charts with a delay to ensure Angular has rendered the DOM elements
        setTimeout(() => {
            let index = 0;
            for (const chart of this.annulusPressureCharts) {
                this.createSingleSeriesChart(index, chart);
                index++;
            }
        }, 50);
    }

    private createSingleSeriesChart(index: number, seriesData: any): void {
        const chartDiv = `seriesChart-${index}`;

        // Dispose of previous chart instance if it exists
        const existingInstance = this.individualChartInstances.get(index);
        if (existingInstance) {
            existingInstance.dispose();
            this.individualChartInstances.delete(index);
        }

        const chartInstance = am4core.create(chartDiv, am4charts.XYChart);
        this.individualChartInstances.set(index, chartInstance);
        chartInstance.width = am4core.percent(100);
        chartInstance.logo.disabled = true;
        chartInstance.paddingBottom = 0;
        chartInstance.marginBottom = 0;

        // Transform data - include mawopPressure for reference
        const chartData = seriesData.data.map((dataPoint: any) => ({
            date: new Date(dataPoint.date),
            pressure: dataPoint.pressure,
            mawopPressure: dataPoint.mawopPressure,
        }));

        chartInstance.data = chartData;

        // Create date axis
        const dateAxis = chartInstance.xAxes.push(new am4charts.DateAxis());
        dateAxis.dateFormats.setKey('day', 'dd MMM yyyy');
        dateAxis.periodChangeDateFormats.setKey('day', 'dd MMM yyyy');
        dateAxis.renderer.labels.template.rotation = -45;
        dateAxis.renderer.labels.template.horizontalCenter = 'right';
        dateAxis.renderer.minGridDistance = 20;
        dateAxis.renderer.labels.template.fontSize = 11;
        dateAxis.renderer.labels.template.fill = am4core.color('#000');

        // Create value axis
        const valueAxis = chartInstance.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = 'Pressure (PSI)';
        valueAxis.renderer.minLabelPosition = 0.01;
        valueAxis.renderer.labels.template.fontSize = 11;
        valueAxis.renderer.labels.template.fill = am4core.color('#000');

        // Get MAWOP value from first data point
        const mawopPressure = seriesData.data[0]?.mawopPressure;

        // Adjust axis max to show MAWOP line if it exists
        if (mawopPressure) {
            const maxPressure = Math.max(
                ...seriesData.data.map((d: any) => d.pressure || 0),
            );
            valueAxis.max = Math.max(maxPressure, mawopPressure) * 1.1;
        }

        // Create line series for actual data
        const lineSeries = chartInstance.series.push(
            new am4charts.LineSeries(),
        );
        lineSeries.dataFields.valueY = 'pressure';
        lineSeries.dataFields.dateX = 'date';
        lineSeries.strokeWidth = 2;
        lineSeries.stroke = am4core.color('#008FD3');

        // Add bullets (dots)
        const bullet = lineSeries.bullets.push(new am4charts.CircleBullet());
        bullet.circle.radius = 3;
        bullet.circle.fill = am4core.color('#008FD3');
        bullet.circle.strokeWidth = 1;
        bullet.circle.stroke = am4core.color('#fff');

        // Add tooltip
        lineSeries.tooltipText =
            'Date: {dateX.formatDate("dd MMM yyyy")}\nPressure: {valueY} PSI';
        lineSeries.tooltip.fontSize = 11;
        lineSeries.tooltip.background.cornerRadius = 4;
        lineSeries.tooltip.background.strokeOpacity = 0;

        // Add chart cursor
        chartInstance.cursor = new am4charts.XYCursor();
        chartInstance.cursor.behavior = 'zoomY';

        // Add MAWOP reference line if available - same as overall chart
        if (mawopPressure !== undefined && mawopPressure !== null) {
            const range = valueAxis.axisRanges.create();
            range.value = mawopPressure;

            // Horizontal target line
            range.grid.visible = true;
            range.grid.strokeOpacity = 1;
            range.grid.strokeWidth = 2;
            range.grid.stroke = am4core.color('#FF0000');
            range.grid.strokeDasharray = '5,5';

            // Label positioned inside plot area, above the line
            range.label.text = `MAWOP: ${mawopPressure} PSI`;
            range.label.fontSize = 11;
            range.label.fontWeight = 'bold';
            range.label.fill = am4core.color('#FF0000');

            range.label.inside = true; // render inside plot, not on axis
            range.label.location = 1; // anchor at top edge of the range (above the line)
            range.label.horizontalCenter = 'middle';
            range.label.textAlign = 'middle';
            range.label.dy = -10; // lift it above the line; tweak to your spacing
            range.label.padding(0, 6, 0, 6); // small horizontal padding
            // Optional: ensure vertical alignment is consistent
            // (Some themes respect valign; if not, dy is enough)
            // @ts-ignore
            range.label.valign = 'top';
        }

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    private createDefaultChart(): void {
        this.hasOverallData = false;

        if (this.overallChartInstance) {
            this.overallChartInstance.dispose();
            this.overallChartInstance = null;
        }

        // Clear the chart div and show "No data" message
        const chartDiv = document.getElementById('chartdiv');
        if (chartDiv) {
            chartDiv.innerHTML =
                '<div class="flex items-center justify-center h-full text-gray-500 text-lg">No data available</div>';
        }
    }

    onTabChanged(tabIndex: number): void {
        // Tab 0 = Details, Tab 1 = Overall
        if (tabIndex === 0) {
            // Details tab selected - recreate individual charts
            if (
                this.currentChartResponse &&
                this.annulusPressureCharts.length > 0
            ) {
                // Dispose existing charts
                for (const chart of this.individualChartInstances.values()) {
                    chart.dispose();
                }
                this.individualChartInstances.clear();

                // Recreate with a delay to ensure DOM elements are visible
                setTimeout(() => {
                    let index = 0;
                    for (const chart of this.annulusPressureCharts) {
                        this.createSingleSeriesChart(index, chart);
                        index++;
                    }
                }, 100);
            }
        } else if (tabIndex === 1) {
            // Overall tab selected - clean recreation to guarantee layout calculation
            if (this.currentChartResponse && this.hasOverallData) {
                setTimeout(() => {
                    this.createOverallChart(this.currentChartResponse!);
                }, 50);
            } else {
                setTimeout(() => {
                    this.createDefaultChart();
                }, 50);
            }
        }
    }

    ngOnDestroy(): void {
        // Dispose of the overall chart instance
        if (this.overallChartInstance) {
            this.overallChartInstance.dispose();
            this.overallChartInstance = null;
        }

        // Dispose of all individual chart instances
        for (const chart of this.individualChartInstances.values()) {
            chart.dispose();
        }
        this.individualChartInstances.clear();
    }

    private loadPlatformsData(): void {
        this.aprService.getAprPlatforms().subscribe({
            next: (fieldsData: FieldData) => {
                // Transform API data to tree structure
                const treeData: TreeNode[] = fieldsData.map((field) => ({
                    name: field.name,
                    longName: field.longName,
                    id: field.fieldId,
                    level: 'Field' as const,
                    children: field.children.map((platform) => ({
                        name: platform.name,
                        longName: platform.longName,
                        id: platform.platformId,
                        level: 'Platform' as const,
                        children:
                            platform.children?.map((wellString) => ({
                                name: wellString.name,
                                longName: wellString.longName,
                                id: wellString.wellStringId,
                                level: 'WellString' as const,
                            })) || [],
                    })),
                }));

                this.dataSource.data = treeData;

                // Auto expand the first parent node only
                setTimeout(() => {
                    if (this.treeControl.dataNodes.length > 0) {
                        const firstNode = this.treeControl.dataNodes[0];
                        this.treeControl.expand(firstNode);

                        // Find first platform and expand it
                        const firstPlatform = this.treeControl.dataNodes.find(
                            (node) => node.nodeLevel === 'Platform',
                        );
                        if (firstPlatform) {
                            this.treeControl.expand(firstPlatform);
                        }

                        // Find first well string and open its graph
                        const firstWellString = this.treeControl.dataNodes.find(
                            (node) => node.nodeLevel === 'WellString',
                        );
                        if (firstWellString) {
                            this.openGraph(firstWellString.id, 'WellString');
                        }
                    }
                }, 0);
            },
            error: (error) => {
                console.error('Failed to load platforms data:', error);
                // Fallback to empty data
                this.dataSource.data = [];
            },
        });
    }

    hasChild = (_: number, node: FlatTreeNode) => node.expandable;
}
